import type { CaptionHighlightMode, CaptionStyle } from "@teksta/shared-types";
import type { OverlayFrame } from "mediatools";
import { type Cue, type CueWord, type LaidOutWord, layoutCueWords } from "subtitletools";
// `LaidOutWord extends CueWord`, adding `leadingSpace` — the word-layout paths
// use it to reproduce the source text's real spacing (no space injected between
// abutting tokens, e.g. Thai). Helpers that only read timing/text stay on
// `CueWord`.

/**
 * Concrete caption styles for the client-side burn-in. `@teksta/shared-types` is
 * type-only, so the preset *values* live here in the app layer; the `CaptionStyle`
 * *shape* is the canonical one shared with the server-side (Remotion) renderer.
 *
 * Sizes are relative (fractions of video height/width) so a preset looks the same
 * at 720p or 4K — see {@link drawStyledCaption} for how they resolve to pixels.
 */
export interface CaptionPreset {
  id: string;
  label: string;
  style: CaptionStyle;
}

const TEKSTA_GREEN = "#34d399";

export const CAPTION_PRESETS: CaptionPreset[] = [
  {
    id: "classic",
    label: "Classic",
    style: {
      font: "'Inter', system-ui, sans-serif",
      fontSizeRel: 0.055,
      fontWeight: 600,
      color: "#ffffff",
      align: "center",
      position: "bottom",
      offsetRel: 0.08,
      stroke: { color: "#000000", widthRel: 0.12 },
      background: null,
      shadow: null,
      highlight: { mode: "none", color: TEKSTA_GREEN },
      fade: null,
      maxWidthRel: 0.86,
    },
  },
  {
    id: "boxed",
    label: "Boxed",
    style: {
      font: "'Inter', system-ui, sans-serif",
      fontSizeRel: 0.05,
      fontWeight: 600,
      color: "#ffffff",
      align: "center",
      position: "bottom",
      offsetRel: 0.08,
      stroke: { color: "#000000", widthRel: 0 },
      background: { color: "rgba(0,0,0,0.66)", paddingRel: 0.35, radiusRel: 0.25 },
      shadow: null,
      highlight: { mode: "none", color: TEKSTA_GREEN },
      fade: null,
      maxWidthRel: 0.8,
    },
  },
  {
    id: "creator-pop",
    label: "Creator pop",
    style: {
      font: "'Inter', system-ui, sans-serif",
      fontSizeRel: 0.064,
      fontWeight: 900,
      color: "#ffffff",
      align: "center",
      position: "bottom",
      offsetRel: 0.1,
      stroke: { color: "#000000", widthRel: 0.14 },
      background: null,
      shadow: { color: "rgba(0,0,0,0.55)", offsetYRel: 0.1, blurRel: 0.04 },
      highlight: {
        mode: "basic",
        color: "#111827",
        box: { color: "#facc15", paddingRel: 0.2, radiusRel: 0.18 },
        pop: true,
      },
      fade: null,
      maxWidthRel: 0.84,
    },
  },
  {
    id: "bold-yellow",
    label: "Bold yellow",
    style: {
      font: "'Inter', system-ui, sans-serif",
      fontSizeRel: 0.062,
      fontWeight: 800,
      color: "#ffe44d",
      align: "center",
      position: "bottom",
      offsetRel: 0.1,
      stroke: { color: "#000000", widthRel: 0.16 },
      background: null,
      shadow: null,
      highlight: { mode: "none", color: TEKSTA_GREEN },
      fade: null,
      maxWidthRel: 0.84,
    },
  },
  {
    id: "top-banner",
    label: "Top banner",
    style: {
      font: "'Inter', system-ui, sans-serif",
      fontSizeRel: 0.048,
      fontWeight: 600,
      color: "#ffffff",
      align: "center",
      position: "top",
      offsetRel: 0.06,
      stroke: { color: "#000000", widthRel: 0 },
      background: { color: "rgba(0,0,0,0.66)", paddingRel: 0.35, radiusRel: 0.25 },
      shadow: null,
      highlight: { mode: "none", color: TEKSTA_GREEN },
      fade: null,
      maxWidthRel: 0.86,
    },
  },
  {
    id: "minimal",
    label: "Minimal",
    style: {
      font: "'Inter', system-ui, sans-serif",
      fontSizeRel: 0.046,
      fontWeight: 500,
      color: "#ffffff",
      align: "center",
      position: "bottom",
      offsetRel: 0.06,
      stroke: { color: "rgba(0,0,0,0.55)", widthRel: 0.08 },
      background: null,
      shadow: null,
      highlight: { mode: "none", color: TEKSTA_GREEN },
      fade: null,
      maxWidthRel: 0.9,
    },
  },
];

type Ctx2D = OffscreenCanvasRenderingContext2D | CanvasRenderingContext2D;

/** Greedy word wrap to `maxWidth` px; assumes `ctx.font` is already set. */
function wrapLines(ctx: Ctx2D, text: string, maxWidth: number): string[] {
  const lines: string[] = [];
  for (const paragraph of text.split("\n")) {
    const words = paragraph.split(/\s+/).filter(Boolean);
    if (words.length === 0) {
      lines.push("");
      continue;
    }
    let line = words[0] as string;
    for (let i = 1; i < words.length; i++) {
      const word = words[i] as string;
      const candidate = `${line} ${word}`;
      if (ctx.measureText(candidate).width > maxWidth && line) {
        lines.push(line);
        line = word;
      } else {
        line = candidate;
      }
    }
    lines.push(line);
  }
  return lines;
}

function roundRect(ctx: Ctx2D, x: number, y: number, w: number, h: number, r: number) {
  const radius = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + w, y, x + w, y + h, radius);
  ctx.arcTo(x + w, y + h, x, y + h, radius);
  ctx.arcTo(x, y + h, x, y, radius);
  ctx.arcTo(x, y, x + w, y, radius);
  ctx.closePath();
}

const clamp01 = (value: number) => Math.min(1, Math.max(0, value));
const finiteDuration = (value: number) => (Number.isFinite(value) ? Math.max(0, value) : 0);
const finiteRel = (value: number) => (Number.isFinite(value) ? Math.max(0, value) : 0);

function withGlobalAlpha(ctx: Ctx2D, alpha: number, draw: () => void) {
  const clamped = clamp01(alpha);
  if (clamped <= 0) return;
  const previous = ctx.globalAlpha;
  ctx.globalAlpha = previous * clamped;
  try {
    draw();
  } finally {
    ctx.globalAlpha = previous;
  }
}

function withTextShadow(ctx: Ctx2D, style: CaptionStyle, fontSize: number, draw: () => void) {
  const shadow = style.shadow;
  if (!shadow) {
    draw();
    return;
  }

  const previousColor = ctx.shadowColor;
  const previousBlur = ctx.shadowBlur;
  const previousOffsetX = ctx.shadowOffsetX;
  const previousOffsetY = ctx.shadowOffsetY;
  ctx.shadowColor = shadow.color;
  ctx.shadowBlur = finiteRel(shadow.blurRel) * fontSize;
  ctx.shadowOffsetX = 0;
  ctx.shadowOffsetY = finiteRel(shadow.offsetYRel) * fontSize;
  try {
    draw();
  } finally {
    ctx.shadowColor = previousColor;
    ctx.shadowBlur = previousBlur;
    ctx.shadowOffsetX = previousOffsetX;
    ctx.shadowOffsetY = previousOffsetY;
  }
}

function withPlainCanvasState(ctx: Ctx2D, draw: () => void) {
  ctx.save();
  ctx.shadowColor = "transparent";
  ctx.shadowBlur = 0;
  ctx.shadowOffsetX = 0;
  ctx.shadowOffsetY = 0;
  ctx.globalCompositeOperation = "source-over";
  try {
    draw();
  } finally {
    ctx.restore();
  }
}

/**
 * Linear cue-edge alpha for {@link CaptionStyle.fade}. Durations are absolute
 * seconds, and if fade-in + fade-out exceeds the cue span both are scaled down
 * proportionally so the caption can still reach full opacity.
 */
export function captionFadeAlpha(
  style: CaptionStyle,
  cueStart: number,
  cueEnd: number,
  time: number
): number {
  if (time < cueStart || time >= cueEnd) return 0;
  const fade = style.fade;
  if (!fade) return 1;

  const duration = cueEnd - cueStart;
  if (!(duration > 0)) return 0;

  let inSec = finiteDuration(fade.inSec);
  let outSec = finiteDuration(fade.outSec);
  const total = inSec + outSec;
  if (total > duration && total > 0) {
    const scale = duration / total;
    inSec *= scale;
    outSec *= scale;
  }

  const elapsed = time - cueStart;
  const remaining = cueEnd - time;
  let alpha = 1;
  if (inSec > 0) alpha = Math.min(alpha, elapsed / inSec);
  if (outSec > 0) alpha = Math.min(alpha, remaining / outSec);
  return clamp01(alpha);
}

/** Resolve the font and baseline onto `ctx`; returns the px font size. */
function applyFont(ctx: Ctx2D, style: CaptionStyle, h: number): number {
  const fontSize = style.fontSizeRel * h;
  ctx.font = `${style.fontWeight} ${fontSize}px ${style.font}`;
  ctx.textBaseline = "alphabetic";
  return fontSize;
}

/** Top y of the text block for the style's vertical position. */
function blockTop(style: CaptionStyle, h: number, blockHeight: number): number {
  const offset = style.offsetRel * h;
  if (style.position === "top") return offset;
  if (style.position === "center") return (h - blockHeight) / 2;
  return h - offset - blockHeight;
}

/** Resolved geometry for a caption text block: the block box (bare text,
 * no background padding) and a function giving each line's left x. */
export interface CaptionBlockLayout {
  /** Top y of the first text line. */
  top: number;
  /** Left x of the block (its widest line's left edge). */
  left: number;
  /** Width of the widest line. */
  width: number;
  /** Total text height (lineHeight × lineCount). */
  height: number;
  /** Left x for a line of width `lineWidth`, honoring `style.align`. */
  lineX(lineWidth: number): number;
}

export function layoutCaptionBlock(
  style: CaptionStyle,
  w: number,
  h: number,
  widest: number,
  blockHeight: number,
  fontSize: number
): CaptionBlockLayout {
  const maxWidth = style.maxWidthRel * w;
  const marginX = (w - maxWidth) / 2;
  const placement = style.placement;
  const pad = placement && style.background ? fontSize * style.background.paddingRel : 0;
  const padW = widest + pad * 2;
  const padH = blockHeight + pad * 2;
  // Horizontal anchor under placement follows `align`: left pins the block's
  // LEFT edge at xRel (labels of different lengths line up and grow rightward),
  // right pins the right edge, center pins the center. Vertical stays
  // center-anchored.
  const anchorLeft = placement
    ? style.align === "left"
      ? placement.xRel * w
      : style.align === "right"
        ? placement.xRel * w - padW
        : placement.xRel * w - padW / 2
    : 0;
  const padLeft = placement ? Math.min(Math.max(anchorLeft, 0), Math.max(0, w - padW)) : 0;
  const padTop = placement
    ? Math.min(Math.max(placement.yRel * h - padH / 2, 0), Math.max(0, h - padH))
    : 0;
  const left = placement ? padLeft + pad : 0;
  const lineX = (lineWidth: number) => {
    if (placement) {
      if (style.align === "left") return left;
      if (style.align === "right") return left + widest - lineWidth;
      return left + (widest - lineWidth) / 2;
    }
    if (style.align === "left") return marginX;
    if (style.align === "right") return w - marginX - lineWidth;
    return (w - lineWidth) / 2;
  };

  return {
    top: placement ? padTop + pad : blockTop(style, h, blockHeight),
    left: placement ? left : lineX(widest),
    width: widest,
    height: blockHeight,
    lineX,
  };
}

/** Draw the background box behind a centered/aligned text block, if any. */
function drawBackgroundBox(
  ctx: Ctx2D,
  style: CaptionStyle,
  fontSize: number,
  layout: CaptionBlockLayout
) {
  if (!style.background) return;
  const pad = fontSize * style.background.paddingRel;
  const boxW = layout.width + pad * 2;
  const boxH = layout.height + pad * 2;
  ctx.fillStyle = style.background.color;
  roundRect(
    ctx,
    layout.left - pad,
    layout.top - pad,
    boxW,
    boxH,
    fontSize * style.background.radiusRel
  );
  ctx.fill();
}

function strokeWord(
  ctx: Ctx2D,
  style: CaptionStyle,
  strokeWidth: number,
  text: string,
  x: number,
  baseline: number
) {
  if (strokeWidth <= 0) return;
  ctx.lineWidth = strokeWidth;
  ctx.strokeStyle = style.stroke.color;
  ctx.lineJoin = "round";
  ctx.miterLimit = 2;
  ctx.strokeText(text, x, baseline);
}

/**
 * Draw a styled caption onto a 2D context sized `w`×`h`, resolving the relative
 * units in {@link CaptionStyle} to pixels. Whole-cue text, no word highlighting —
 * used for the cached (`highlight.mode === "none"`) path.
 */
export function drawStyledCaption(
  ctx: Ctx2D,
  text: string,
  style: CaptionStyle,
  w: number,
  h: number,
  fadeAlpha = 1
) {
  const fontSize = applyFont(ctx, style, h);
  const maxWidth = style.maxWidthRel * w;

  const lines = wrapLines(ctx, text, maxWidth);
  if (lines.length === 0) return;

  withGlobalAlpha(ctx, fadeAlpha, () => {
    const lineHeight = fontSize * 1.2;
    const blockHeight = lineHeight * lines.length;
    const measuredLines = lines.map((line) => ({ line, width: ctx.measureText(line).width }));
    const widest = Math.max(...measuredLines.map(({ width }) => width));
    const layout = layoutCaptionBlock(style, w, h, widest, blockHeight, fontSize);
    drawBackgroundBox(ctx, style, fontSize, layout);

    ctx.textAlign = "left";

    const strokeWidth = style.stroke.widthRel * fontSize;
    withTextShadow(ctx, style, fontSize, () => {
      measuredLines.forEach(({ line, width }, i) => {
        const baseline = layout.top + lineHeight * i + fontSize;
        const lineX = layout.lineX(width);
        strokeWord(ctx, style, strokeWidth, line, lineX, baseline);
        ctx.fillStyle = style.color;
        ctx.fillText(line, lineX, baseline);
      });
    });
  });
}

interface PositionedWord {
  word: LaidOutWord;
  width: number;
}

/**
 * Greedy word-aware wrap to `maxWidth`; assumes `ctx.font` is set. A space is
 * only charged between words that had one in the source (`leadingSpace`), so
 * abutting tokens (e.g. Thai syllables) pack with no gap; a word that starts a
 * line never carries a leading space.
 */
function wrapWords(
  ctx: Ctx2D,
  words: LaidOutWord[],
  maxWidth: number,
  spaceWidth: number
): PositionedWord[][] {
  const lines: PositionedWord[][] = [];
  let line: PositionedWord[] = [];
  let lineWidth = 0;
  for (const word of words) {
    const width = ctx.measureText(word.text).width;
    const gap = line.length && word.leadingSpace ? spaceWidth : 0;
    if (line.length && lineWidth + gap + width > maxWidth) {
      lines.push(line);
      line = [];
      lineWidth = 0;
    }
    // After a wrap this word starts the line, so its leading space drops.
    const leadGap = line.length && word.leadingSpace ? spaceWidth : 0;
    line.push({ word, width });
    lineWidth += leadGap + width;
  }
  if (line.length) lines.push(line);
  return lines;
}

const lineWidthOf = (line: PositionedWord[], spaceWidth: number) =>
  line.reduce((sum, pw, i) => sum + pw.width + (i && pw.word.leadingSpace ? spaceWidth : 0), 0);

const isActiveWord = (word: CueWord, time: number) => time >= word.start && time < word.end;

const hasActiveWordStyle = (style: CaptionStyle) =>
  Boolean(style.highlight.box || style.highlight.pop);

const usesTimeDependentWords = (style: CaptionStyle) =>
  style.highlight.mode !== "none" || hasActiveWordStyle(style);

function activeWordScale(style: CaptionStyle, word: CueWord, time: number): number {
  if (!style.highlight.pop || !isActiveWord(word, time)) return 1;
  const pulseSec = 0.18;
  const elapsed = Math.max(0, time - word.start);
  const duration = Math.max(0.001, Math.min(pulseSec, word.end - word.start));
  const t = clamp01(elapsed / duration);
  return 1 + 0.08 * (1 - t) ** 3;
}

function withWordScale(
  ctx: Ctx2D,
  x: number,
  baseline: number,
  width: number,
  fontSize: number,
  scale: number,
  draw: () => void
) {
  if (scale === 1) {
    draw();
    return;
  }

  const centerX = x + width / 2;
  const centerY = baseline - fontSize / 2;
  ctx.save();
  ctx.translate(centerX, centerY);
  ctx.scale(scale, scale);
  ctx.translate(-centerX, -centerY);
  try {
    draw();
  } finally {
    ctx.restore();
  }
}

function drawActiveWordBox(
  ctx: Ctx2D,
  style: CaptionStyle,
  text: string,
  x: number,
  baseline: number,
  width: number,
  fontSize: number,
  scale: number
) {
  const box = style.highlight.box;
  if (!box) return;
  const pad = finiteRel(box.paddingRel) * fontSize;
  // Bracket the box on the font's real vertical extent (baseline→top and
  // baseline→bottom), not the em box. Using `fontSize` as the band put the top a
  // full em above the baseline — well above the actual letters — and stopped
  // ~0.08em below it, starving the descenders. fontBoundingBox is glyph-
  // independent, so the box keeps a steady height as the active word changes.
  const m = ctx.measureText(text);
  const ascent = m.fontBoundingBoxAscent || fontSize * 0.82;
  const descent = m.fontBoundingBoxDescent || fontSize * 0.21;
  const boxX = x - pad;
  const boxY = baseline - ascent - pad;
  const boxW = width + pad * 2;
  const boxH = ascent + descent + pad * 2;
  withPlainCanvasState(ctx, () => {
    withWordScale(ctx, x, baseline, width, fontSize, scale, () => {
      ctx.fillStyle = box.color;
      roundRect(ctx, boxX, boxY, boxW, boxH, finiteRel(box.radiusRel) * fontSize);
      ctx.fill();
    });
  });
}

/** Fill style for a word given the highlight mode and current `time`. */
function wordFill(
  ctx: Ctx2D,
  mode: CaptionHighlightMode,
  base: string,
  highlight: string,
  word: CueWord,
  time: number,
  x: number,
  width: number
): string | CanvasGradient {
  if (mode === "basic") return time >= word.start && time < word.end ? highlight : base;
  if (mode === "creeping") return time >= word.start ? highlight : base;
  if (mode === "sliding") {
    if (time >= word.end) return highlight;
    if (time < word.start) return base;
    const span = word.end - word.start;
    const progress = span > 0 ? (time - word.start) / span : 1;
    const p = Math.min(Math.max(progress, 0.0001), 0.9999);
    const grad = ctx.createLinearGradient(x, 0, x + width, 0);
    grad.addColorStop(0, highlight);
    grad.addColorStop(p, highlight);
    grad.addColorStop(p, base); // hard edge — the sweep front
    grad.addColorStop(1, base);
    return grad;
  }
  return base;
}

/**
 * Draw a caption with per-word highlighting at `time` (seconds). Lays words out
 * individually so each can take the base or highlight color; `sliding` fills the
 * active word with a gradient that sweeps as it's spoken. Per-frame (the frame
 * depends on `time`), so callers shouldn't cache the result.
 */
function drawHighlightedCaption(
  ctx: Ctx2D,
  words: LaidOutWord[],
  style: CaptionStyle,
  w: number,
  h: number,
  time: number,
  fadeAlpha = 1
) {
  if (words.length === 0) return;
  const fontSize = applyFont(ctx, style, h);
  ctx.textAlign = "left";
  const maxWidth = style.maxWidthRel * w;
  const spaceWidth = ctx.measureText(" ").width;

  const lines = wrapWords(ctx, words, maxWidth, spaceWidth);
  const lineHeight = fontSize * 1.2;
  const blockHeight = lineHeight * lines.length;

  const widest = Math.max(...lines.map((line) => lineWidthOf(line, spaceWidth)));
  const layout = layoutCaptionBlock(style, w, h, widest, blockHeight, fontSize);
  withGlobalAlpha(ctx, fadeAlpha, () => {
    drawBackgroundBox(ctx, style, fontSize, layout);

    const positionedLines = lines.map((line, i) => {
      const baseline = layout.top + lineHeight * i + fontSize;
      const total = lineWidthOf(line, spaceWidth);
      return { line, baseline, x: layout.lineX(total) };
    });

    for (const { line, baseline, x: lineX } of positionedLines) {
      let x = lineX;
      for (let i = 0; i < line.length; i++) {
        const { word, width } = line[i] as PositionedWord;
        if (i > 0 && word.leadingSpace) x += spaceWidth;
        if (isActiveWord(word, time)) {
          drawActiveWordBox(
            ctx,
            style,
            word.text,
            x,
            baseline,
            width,
            fontSize,
            activeWordScale(style, word, time)
          );
        }
        x += width;
      }
    }

    const strokeWidth = style.stroke.widthRel * fontSize;
    const mode = style.highlight.mode;
    withTextShadow(ctx, style, fontSize, () => {
      for (const { line, baseline, x: lineX } of positionedLines) {
        let x = lineX;
        for (let i = 0; i < line.length; i++) {
          const { word, width } = line[i] as PositionedWord;
          if (i > 0 && word.leadingSpace) x += spaceWidth;
          const scale = activeWordScale(style, word, time);
          withWordScale(ctx, x, baseline, width, fontSize, scale, () => {
            strokeWord(ctx, style, strokeWidth, word.text, x, baseline);
            ctx.fillStyle = wordFill(
              ctx,
              mode,
              style.color,
              style.highlight.color,
              word,
              time,
              x,
              width
            );
            ctx.fillText(word.text, x, baseline);
          });
          x += width;
        }
      }
    });
  });
}

export interface CaptionBox {
  left: number;
  top: number;
  width: number;
  height: number;
}

/**
 * Measure the on-screen bounds of `text` rendered with `style` in a `w`×`h` box
 * (the video's content rect), using the *same* layout as {@link drawStyledCaption}
 * — so the editor's selection box matches the rendered caption exactly. Includes
 * the background box padding when a background is set. Returns null for empty text.
 */
export function measureCaptionBox(
  style: CaptionStyle,
  text: string,
  w: number,
  h: number
): CaptionBox | null {
  const ctx = new OffscreenCanvas(1, 1).getContext("2d");
  if (!ctx) return null;
  const fontSize = applyFont(ctx, style, h);
  const maxWidth = style.maxWidthRel * w;

  // Lay the text out with the SAME path the renderer (drawCaptionFrame) will use,
  // or the box drifts from the drawn caption on long, multi-line cues: the
  // highlight paths wrap word-by-word (wrapWords), the plain path wraps the whole
  // string (wrapLines), and the two can break into different line counts/widths.
  let lineCount: number;
  let widest: number;
  if (usesTimeDependentWords(style)) {
    const spaceWidth = ctx.measureText(" ").width;
    const lines = wrapWords(ctx, layoutCueWords({ text, start: 0, end: 0 }), maxWidth, spaceWidth);
    if (lines.length === 0) return null;
    lineCount = lines.length;
    widest = Math.max(...lines.map((line) => lineWidthOf(line, spaceWidth)));
  } else {
    const lines = wrapLines(ctx, text, maxWidth);
    if (lines.length === 0) return null;
    lineCount = lines.length;
    widest = Math.max(...lines.map((line) => ctx.measureText(line).width));
  }

  const lineHeight = fontSize * 1.2;
  const blockHeight = lineHeight * lineCount;
  const layout = layoutCaptionBlock(style, w, h, widest, blockHeight, fontSize);
  let left = layout.left;
  let boxTop = layout.top;
  let width = layout.width;
  let height = layout.height;
  if (style.background) {
    const pad = fontSize * style.background.paddingRel;
    left -= pad;
    boxTop -= pad;
    width += pad * 2;
    height += pad * 2;
  }
  return { left, top: boxTop, width, height };
}

/** Index of the cue active at `time` (seconds), or -1. Assumes cues are sorted. */
function activeCueIndex(cues: Cue[], time: number): number {
  for (let i = 0; i < cues.length; i++) {
    const cue = cues[i];
    if (!cue) continue;
    if (time < cue.start) return -1; // sorted: nothing earlier can match
    if (time < cue.end) return i;
  }
  return -1;
}

/** A live caption rendering: the cues plus the resolved style to draw them with. */
export interface CaptionPreviewSpec {
  cues: Cue[];
  style: CaptionStyle;
}

/**
 * Draw the caption active at `time` directly onto `ctx` (cleared by the caller).
 * The single-frame primitive behind the **live preview** — same `CaptionStyle`
 * and draw functions as the burn, so what you see matches the export. Does
 * nothing when no cue is active. (The burn uses {@link createCaptionOverlay}
 * instead, which adds a per-cue rasterization cache for the no-highlight case.)
 */
export function drawCaptionFrame(
  ctx: Ctx2D,
  cues: Cue[],
  style: CaptionStyle,
  time: number,
  w: number,
  h: number
) {
  const index = activeCueIndex(cues, time);
  if (index < 0) return;
  const cue = cues[index];
  if (!cue) return;
  const fadeAlpha = captionFadeAlpha(style, cue.start, cue.end, time);
  if (!usesTimeDependentWords(style)) {
    drawStyledCaption(ctx, cue.text, style, w, h, fadeAlpha);
  } else {
    drawHighlightedCaption(ctx, layoutCueWords(cue), style, w, h, time, fadeAlpha);
  }
}

/**
 * Build a `drawOverlay` for {@link renderWithOverlay} that burns `cues` in with
 * the given style.
 *
 * - No highlight (`mode === "none"`): each cue is rasterized to a transparent
 *   layer **once** and reused for every frame it spans — cost O(cues).
 * - With a highlight mode: the frame depends on playback time, so it's redrawn
 *   per frame; word timings are synthesized once per cue (or taken from the cue
 *   when present).
 */
export function createCaptionOverlay(cues: Cue[], style: CaptionStyle) {
  if (!usesTimeDependentWords(style)) {
    const layers = new Map<number, OffscreenCanvas>();
    return (ctx: OffscreenCanvasRenderingContext2D, frame: OverlayFrame) => {
      const index = activeCueIndex(cues, frame.time);
      if (index < 0) return;
      const cue = cues[index];
      if (!cue) return;
      let layer = layers.get(index);
      if (!layer) {
        layer = new OffscreenCanvas(frame.width, frame.height);
        const layerCtx = layer.getContext("2d");
        if (!layerCtx) return;
        drawStyledCaption(layerCtx, cue.text, style, frame.width, frame.height);
        layers.set(index, layer);
      }
      withGlobalAlpha(ctx, captionFadeAlpha(style, cue.start, cue.end, frame.time), () => {
        ctx.drawImage(layer, 0, 0);
      });
    };
  }

  const wordsByCue = new Map<number, LaidOutWord[]>();
  return (ctx: OffscreenCanvasRenderingContext2D, frame: OverlayFrame) => {
    const index = activeCueIndex(cues, frame.time);
    if (index < 0) return;
    const cue = cues[index];
    if (!cue) return;
    let words = wordsByCue.get(index);
    if (!words) {
      words = layoutCueWords(cue);
      wordsByCue.set(index, words);
    }
    drawHighlightedCaption(
      ctx,
      words,
      style,
      frame.width,
      frame.height,
      frame.time,
      captionFadeAlpha(style, cue.start, cue.end, frame.time)
    );
  };
}
